import type { ScopeContext } from '@sentry/types';

/**
 * File to attach to the exception in Sentry.
 */
export interface ExceptionAttachment {
    /** File name */
    name: string;
    /** Body as a string for the file */
    body: string | Uint8Array;
    /** Mime-type for the attachment */
    type?: string;
}

/**
 * Options to attach to the exception in Sentry.
 */
export type ReportExceptionOptions = Partial<
    Pick<ScopeContext, 'tags' | 'extra' | 'fingerprint'>
> & {
    /** Attachments for the exception */
    attachments?: ExceptionAttachment[];
};

/**
 * A wrapped error allowing the called to add tags and metadata ready for Sentry.
 */
export class ReportException extends Error {
    public options: ReportExceptionOptions;

    constructor(message: string, options?: ReportExceptionOptions) {
        super(message);

        this.options = options || {};
    }

    /**
     * Annotate an error with additional metadata.
     */
    public static annotateError(error: Error, options: ReportExceptionOptions): ReportException {
        const reportError = new ReportException(error.message, options);
        reportError.stack = error.stack || error.message;
        return reportError;
    }
}
